import apiClient from "./api-client";

/**
 * Web Push del dashboard (15-sep-2026): suscripción de ESTE dispositivo a los
 * avisos de conversaciones (handoff, mensajes en modo humano) y numerito del
 * icono. La clave pública VAPID se pide al backend (GET
 * /staff/web-push/public-key) — así rotar claves no exige rebuild del
 * frontend. En iPhone requiere el PWA INSTALADO (iOS 16.4+); en Safari
 * pestaña normal no existe PushManager y el toggle ni se enseña.
 */

export type WebPushStatus = "unsupported" | "denied" | "inactive" | "active";

export function isWebPushSupported(): boolean {
  return (
    "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  );
}

export async function getWebPushStatus(): Promise<WebPushStatus> {
  if (!isWebPushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription ? "active" : "inactive";
  } catch {
    return "inactive";
  }
}

/** applicationServerKey: la clave VAPID viene en base64url. */
function urlBase64ToServerKey(base64url: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return buffer;
}

/**
 * Pide permiso (DEBE llamarse desde un gesto del usuario: iOS lo exige),
 * suscribe el dispositivo y lo registra en el backend.
 */
export async function enableWebPush(): Promise<{ ok: boolean; reason?: string }> {
  if (!isWebPushSupported()) {
    return {
      ok: false,
      reason:
        "Este navegador no soporta notificaciones. En iPhone, instala la app en la pantalla de inicio.",
    };
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "Permiso de notificaciones no concedido" };
  }
  try {
    const keyRes = await apiClient.get<{ success: true; data: { publicKey: string } }>(
      "/staff/web-push/public-key",
    );
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToServerKey(keyRes.data.data.publicKey),
    });
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return { ok: false, reason: "El navegador devolvió una suscripción incompleta" };
    }
    await apiClient.post("/staff/web-push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: "No se pudieron activar las notificaciones" };
  }
}

/** Da de baja este dispositivo (navegador y backend). Mejor esfuerzo. */
export async function disableWebPush(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await apiClient.delete("/staff/web-push/subscribe", { data: { endpoint } });
  } catch {
    /* mejor esfuerzo: si falla, la limpieza 404/410 del backend lo recoge */
  }
}

/**
 * Numerito del icono del PWA instalado (Badging API, ya tipada en lib.dom).
 * Silencioso donde no exista (pestaña normal, navegadores sin soporte).
 * 0 limpia el badge.
 */
export function setAppIconBadge(count: number): void {
  try {
    if (count > 0) {
      navigator.setAppBadge?.(count).catch(() => undefined);
    } else {
      navigator.clearAppBadge?.().catch(() => undefined);
    }
  } catch {
    /* nada */
  }
}
